import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase.js';
import ModernHeader from '../components/modern/ModernHeader.jsx';
import ModernFooter from '../components/modern/ModernFooter.jsx';
import SEO from '../components/modern/SEO.jsx';

export default function WorksPage() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWork, setSelectedWork] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    loadWorks();
  }, []);

  const loadWorks = async () => {
    // SEO & Error handling: Check if Supabase is configured before trying to load
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .storage
        .from('works')
        .list('', { limit: 100, sortBy: { column: 'created_at', order: 'desc' } });

      if (error) throw error;

      // Csoportosítjuk munka név szerint
      const groupedWorks = {};
      data.forEach(file => {
        // Match both old timestamp format and new secure ID format
        const match = file.name.match(/^(.+?)_[a-z0-9]+_\d+\./i);
        if (match) {
          const workName = match[1];
          if (!groupedWorks[workName]) {
            groupedWorks[workName] = [];
          }
          
          const { data: urlData } = supabase.storage
            .from('works')
            .getPublicUrl(file.name);
          
          groupedWorks[workName].push({
            name: file.name,
            url: urlData.publicUrl
          });
        }
      });

      const worksArray = Object.entries(groupedWorks).map(([name, files]) => ({
        name,
        images: files,
        mainImage: files[0]?.url,
        count: files.length
      }));

      setWorks(worksArray);
    } catch (error) {
      console.error('Hiba a munkák betöltésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (work, imageIndex = 0) => {
    setSelectedWork(work);
    setSelectedImageIndex(imageIndex);
    
    // Letiltjuk a scroll-t (nem ugrik fel!)
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  const closeGallery = () => {
    setSelectedWork(null);
    setSelectedImageIndex(0);
    
    // Visszaállítjuk a scroll-t
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  };

  const nextImage = () => {
    if (selectedWork && selectedImageIndex < selectedWork.images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (selectedWork && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedWork) return;

      switch (e.key) {
        case 'Escape':
          closeGallery();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWork, selectedImageIndex]);

  // Touch/Swipe navigation (mobile)
  useEffect(() => {
    if (!selectedWork) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50; // Minimum swipe distance in pixels
      
      if (touchStartX - touchEndX > swipeThreshold) {
        // Swipe left -> next image
        nextImage();
      } else if (touchEndX - touchStartX > swipeThreshold) {
        // Swipe right -> previous image
        prevImage();
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWork, selectedImageIndex]);

  return (
    <>
      {/* SEO: Enhanced meta tags for portfolio/works page */}
      <SEO 
        title="Munkáink - Egyedi bútor referenciák | XL Bútor Kisvárda"
        description="Tekintse meg egyedi bútor projektjeinket: konyhabútorok, szekrények, nappali bútorok. 35+ év tapasztalat, precíz kivitelezés. Ingyenes konzultáció!"
        keywords="egyedi bútor referenciák, konyhabútor képek, szekrény referenciák, nappali bútor galéria, bútor portfólió, XL Bútor munkák"
        url="https://xlbutorbolt.hu/works"
        canonical="https://xlbutorbolt.hu/works"
      />
      <ModernHeader />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-amber-50 to-orange-50 py-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmYjkyM2MiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2djRoLTR2LTRoNHptLTQgNGg0djRoLTR2LTR6bS00LTRoNHY0aC00di00em0tNCA0aDR2NGgtNHYtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-40"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-4">
                PORTFÓLIÓ
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Munkáink
              </h1>
              <p className="text-xl text-gray-600">
                Fedezd fel legújabb egyedi bútor projektjeinket és inspirálódj otthonod berendezéséhez
              </p>
            </div>
          </div>
        </section>

        {/* Works Grid */}
        <section className="container mx-auto px-4 py-16">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-amber-200 border-t-amber-600"></div>
              <p className="text-gray-600 mt-6 text-lg">Képek betöltése...</p>
            </div>
          ) : works.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 text-xl">Még nincsenek feltöltött munkák.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {works.map((work, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                >
                  {/* Image Container */}
                  <div
                    className="relative h-80 cursor-pointer overflow-hidden"
                    onClick={() => openGallery(work, 0)}
                  >
                    <img
                      src={work.mainImage}
                      alt={work.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium">Galéria megtekintése</span>
                        </div>
                      </div>
                    </div>

                    {/* Image Count Badge */}
                    <div className="absolute top-4 right-4 bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      {work.count} kép
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {work.name}
                    </h3>
                    <button
                      onClick={() => openGallery(work, 0)}
                      className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold transition-colors"
                    >
                      <span>Részletek</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Gallery Modal */}
        {selectedWork && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            {selectedImageIndex > 0 && (
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-colors backdrop-blur-sm"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {selectedImageIndex < selectedWork.images.length - 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-colors backdrop-blur-sm"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Image Container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={selectedWork.images[selectedImageIndex]?.url}
                alt={selectedWork.name}
                className="max-w-full max-h-[calc(100vh-120px)] object-contain"
              />
            </div>
            
            {/* Info Bar */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-full z-20">
              <div className="text-center">
                <p className="font-semibold text-lg">{selectedWork.name}</p>
                <p className="text-sm text-gray-300">
                  {selectedImageIndex + 1} / {selectedWork.images.length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModernFooter />
    </>
  );
}
